/**
 * Workspace context — carries transient UI state that many components need:
 *   * live 3D cursor coordinate readout (updated by DrillholeViewer3D)
 *   * output-console log lines (any component can `pushLog(...)`)
 *   * AI drawer + command-palette open state (top-level triggers)
 *
 * Intentionally kept lightweight — persistent workspace layouts (dock/float
 * geometry, saved profiles) live in a separate future context.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const WorkspaceCtx = createContext(null);

const MAX_LOG_LINES = 200;

export function WorkspaceProvider({ children }) {
  const [coord, setCoord] = useState(null); // {x,y,z}
  const [logs, setLogs] = useState([]);      // [{ts, level, message}]
  const [aiOpen, setAiOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const projectCtxRef = useRef(null);        // { id, name, code, commodity }

  const pushLog = useCallback((message, level = "info") => {
    if (!message) return;
    setLogs((prev) => {
      const next = prev.concat([{ ts: Date.now(), level, message: String(message) }]);
      return next.length > MAX_LOG_LINES ? next.slice(-MAX_LOG_LINES) : next;
    });
  }, []);

  const clearLogs = useCallback(() => setLogs([]), []);

  const setProjectContext = useCallback((proj) => { projectCtxRef.current = proj || null; }, []);
  const getProjectContext = useCallback(() => projectCtxRef.current, []);

  const openAI = useCallback(() => setAiOpen(true), []);
  const closeAI = useCallback(() => setAiOpen(false), []);
  const toggleAI = useCallback(() => setAiOpen((v) => !v), []);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const openShortcuts = useCallback(() => setShortcutsOpen(true), []);
  const closeShortcuts = useCallback(() => setShortcutsOpen(false), []);
  const toggleConsole = useCallback(() => setConsoleOpen((v) => !v), []);

  useEffect(() => {
    // Very light global boot log — helpful in the Output Console.
    pushLog("SMIN workspace initialised", "info");
  }, [pushLog]);

  return (
    <WorkspaceCtx.Provider
      value={{
        coord, setCoord,
        logs, pushLog, clearLogs,
        aiOpen, openAI, closeAI, toggleAI,
        paletteOpen, openPalette, closePalette,
        shortcutsOpen, openShortcuts, closeShortcuts,
        consoleOpen, toggleConsole, setConsoleOpen,
        setProjectContext, getProjectContext,
      }}
    >
      {children}
    </WorkspaceCtx.Provider>
  );
}

export function useWorkspace() {
  return useContext(WorkspaceCtx);
}

/** Same as useWorkspace but throws — for components that MUST have a provider. */
export function useWorkspaceRequired() {
  const ctx = useContext(WorkspaceCtx);
  if (!ctx) throw new Error("useWorkspaceRequired must be used inside <WorkspaceProvider>");
  return ctx;
}
